
import Immutable from 'seamless-immutable'
import { getCommonWidget } from '../dashWidgetHelper';
import { commonWidgets, OXXO_DEFAULT_COLORS } from './commonWidgetsOxxo';
import dayjs from 'dayjs';
import { renderForCriticalMoment } from '../dashHelper';
import { buildCommentsMapper } from '@utils/commentHelpers';

// Definimos las regiones que queremos replicar
const REGIONES_OBJETIVO = [
  { id: 9953, name: 'Norte' },
  { id: 13500, name: 'Nuevo León' },
  { id: 13450, name: 'Sur' },
];

const REGIONAL_TOTALS: Record<number, number> = {};
REGIONES_OBJETIVO.forEach(reg => {
  REGIONAL_TOTALS[reg.id] = 0;
});

const WEEKLY_COLORS = {
  green: '#04BF45',
  gray: '#BFBFBF',
  red: '#A62631'
}

const WEEKLY_TOTALS: Record<string, number> = {};

// Calculamos "hoy" y "hace 7 días" globales
const globalToDate = dayjs().format('YYYY-MM-DD');
const globalFromDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');

// Función "Fábrica" que genera los widgets para una región específica
const generateRegionalWidgets = (
  region: { id: any; name: any; },
  idx: number,
  dateRange: { fromDate: string; toDate: string }
) => [

  {
    title: `${region.name}`,
    type: 'label',
    size: [4, 1],
    //config: { color: idx === 0 ? '#B7AADC': undefined },
  },
  getCommonWidget({
    commonWidgets,
    name: 'alertBarByWeekFull',
    title: `Alertas por semana - ${region.name}`,
    key: `stacked-alerts-weekly-full-final-${region.id}`,
    size: [3, 4],
    extraConfigs: {
      groupMode: 'stacked',
      groupKeys: ['noDetonated', 'rescue', 'wow'],
      groupLabels: { noDetonated: 'Sin Alerta', rescue: 'Rescate', wow: 'WOW' },
      colors: {
        noDetonated: WEEKLY_COLORS.gray,//WEEKLY_COLORS.gray, 
        wow: WEEKLY_COLORS.green,//WEEKLY_COLORS.green, 
        rescue: WEEKLY_COLORS.red,//WEEKLY_COLORS.red 
      },
      showValues: true,
      showLegend: true,
      percentageScale: false,
      label: {
        show: true, position: 'right', color: '#333333',
        formatter: (params: any) => {
          if (!params.value || params.value === 0) return '';
          return new Intl.NumberFormat('es-MX').format(params.value);
        }
      },
      labelLayout: { moveOverlap: 'shiftY' },
      decimals: 0
    },
    extras: {
      uniqueId: `widget-weekly-${region.id}`,
      geoLocationInclude: [region.id]
    }
  }),

  getCommonWidget({
    commonWidgets: commonWidgets,
    name: 'gaugeGeneric',
    title: `Satisfacción - ${region.name}`,
    size: [1, 2],
    key: `alert-week-${region.id}`,
    indicator: 'satisfaction',
    extras: {
      uniqueId: `widget-${region.id}`,
      geoLocationInclude: [region.id]
    }
  }),

  getCommonWidget({
    commonWidgets: commonWidgets,
    name: 'gaugeGeneric',
    title: `NPS - ${region.name}`,
    size: [1, 2],
    key: `alert-week-${region.id}`,
    indicator: 'willingness-to-recommend',
    extras: {
      uniqueId: `widget-${region.id}`,
      geoLocationInclude: [region.id]
    }
  }),

  getCommonWidget({
    commonWidgets: commonWidgets,
    title: `Top 5 Positivos - ${region.name}`,
    name: 'lastComments',
    size: [2, 3],
    indicator: 'SENTIMENT_COMMENTS',
    key: `last-comments-pos-${region.id}`,
    mapper: buildCommentsMapper({
      type: 'positive',
      limit: 5,
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate
    }),
    extraConfigs: {
      maxCommentSize: 120,
      layout: 'insight',
      showDateTimePill: true,
      showGeoLocation: false
    },
    extraIndicatorConfigs: {
      sort: { 'sortBy': 'score', value: 'desc', },
    },
    extras: {
      geoLocationInclude: [region.id],
      limit: 30,
      order: 'desc',
      numberAnswerFrom: 1
    }
  }),

  getCommonWidget({
    commonWidgets: commonWidgets,
    title: `Top 5 Áreas de Oportunidad - ${region.name}`,
    name: 'lastComments',
    size: [2, 3],
    indicator: 'SENTIMENT_COMMENTS',
    key: `last-comments-neg-${region.id}`,
    extraConfigs: {
      maxCommentSize: 120,
      layout: 'insight',
      showDateTimePill: true,
      showGeoLocation: false
    },
    extraIndicatorConfigs: {
      sort: { 'sortBy': 'score', value: 'asc', },
    },

    extras: {
      geoLocationInclude: [region.id],
      limit: 30,
      order: 'asc',
      numberAnswerTo: 0
    },
    mapper: buildCommentsMapper({
      type: 'negative',
      limit: 5,
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate
    }),
  })
];

// EXPORTACIÓN PRINCIPAL (Limpia y directa)
export default {
  title: 'Dashboard semanal',
  description: 'Dashboard semanal',
  backgroundClassName: (filters: any) => 'epsilon-hero-bg',

  widgets: (filters: any) => {
    // 1. Calculamos el rango por defecto
    const defaultRange = {
      fromDate: filters?.fromDate
        ? dayjs(filters.fromDate).format('YYYY-MM-DD')
        : globalFromDate,
      toDate: filters?.toDate
        ? dayjs(filters.toDate).format('YYYY-MM-DD')
        : globalToDate
    };

    // 2. Inyector que ataca a nivel raíz y nivel extras
    const injectDefaultDates = (wdg: any) => {
      const dateExtras = {
        fromDate: defaultRange.fromDate,
        toDate: defaultRange.toDate,
        dateFrom: defaultRange.fromDate,
        dateTo: defaultRange.toDate
      }

      return {
        ...wdg,
        extras: {
          ...wdg.extras,
          ...dateExtras
        },
        filters: {
          ...wdg.filters,
          ...defaultRange
        },
        config: wdg.config
          ? {
              ...wdg.config,
              ...(wdg.config.indicators
                ? {
                    indicators: wdg.config.indicators.map((ind: any) => ({
                      ...ind,
                      extras: {
                        ...ind.extras,
                        ...dateExtras
                      }
                    }))
                  }
                : {})
            }
          : wdg.config
      };
    };

    // 3. Declaramos un arreglo lineal (sin meterlo en funciones raras)
    const generalWidgets = [

      {
        title: 'Generales',
        type: 'label',
        size: [4, 1],
        config: { color: '#B7AADC' },
      },

      getCommonWidget({
        commonWidgets,
        name: 'alertPieNationalFull',
        title: 'Distribución Nacional de Alertas',
        size: [2, 2],
        key: 'alert-pie-national-fixed',
        extraConfigs: {
          colors: [WEEKLY_COLORS.gray, WEEKLY_COLORS.green, WEEKLY_COLORS.red],
          showValues: true,
          showLegend: true,
          showCountLabel: true,
        },
        extras: {
          uniqueId: 'pie-alert-national-total-vfinal',
          groupByTopLocation: true,
        },
      }),

      getCommonWidget({
        commonWidgets,
        name: 'alertBarByRegionFull',
        title: 'Distribución de Alertas por Región (Periodo Actual)',
        key: 'stacked-alerts-regional-global-final',
        size: [2, 2],
        extraConfigs: {
          groupMode: 'stacked',
          groupKeys: ['noDetonated', 'rescue', 'wow'],
          groupLabels: { noDetonated: 'Sin Alerta', rescue: 'Rescate', wow: 'WOW' },
          colors: { noDetonated: WEEKLY_COLORS.gray, wow: WEEKLY_COLORS.green, rescue: WEEKLY_COLORS.red },
          showValues: true,
          showLegend: true,
          percentageScale: false,
          label: {
            show: true, position: 'right', color: '#333333',
            formatter: (params: any) => {
              if (!params.value || params.value === 0) return '';
              return new Intl.NumberFormat('es-MX').format(params.value);
            }
          },
          labelLayout: { moveOverlap: 'shiftY' },
          decimals: 0
        },
        extras: {
          uniqueId: 'widget-regional-global-v1',
        }
      }),

      getCommonWidget({
        commonWidgets: commonWidgets,
        name: 'pieGenericMedium',
        title: '¿Cuál (es) de estos servicios le ofrecieron?',
        size: [2, 2],
        showSample: false,
        indicator: 'ADDICIONAL_SERVICE_OPTIONS',
        criticalMomentInclude: [1, 11702],
        // BUG CORREGIDO: Fusionamos los filtros originales con los de defaultRange
        render: (f: any) => renderForCriticalMoment({ ...f, ...defaultRange }, [29, 1, 11702]),
      }),

      getCommonWidget({
        commonWidgets: commonWidgets,
        name: 'pieGenericMedium',
        title: '¿Durante tu visita te ofrecieron alguno de los siguientes servicios complementarios?',
        size: [2, 2],
        showSample: false,
        indicator: 'COMPLEMENTARY_SERVICE_OPTIONS',
        criticalMomentInclude: [1, 11702],
        // BUG CORREGIDO: Fusionamos los filtros originales con los de defaultRange
        render: (f: any) => renderForCriticalMoment({ ...f, ...defaultRange }, [29, 1, 11702]),
      }),

      getCommonWidget({
        commonWidgets: commonWidgets,
        name: 'rankingByTagBest',
        title: 'Sentimiento positivo con más frecuencia',
        size: [2, 1],
        indicator: 'sentiment',
        criticalMomentInclude: [1],
        extras: { trunc: 'HALF_DOWN', topSize: 3 },
        extraConfigs: { color: OXXO_DEFAULT_COLORS.VERDE }
      }),

      getCommonWidget({
        commonWidgets: commonWidgets,
        name: 'rankingByTagWorst',
        title: 'Sentimiento negativo con más frecuencia',
        size: [2, 1],
        indicator: 'sentiment',
        criticalMomentInclude: [1],
        extras: { trunc: 'HALF_UP', bottomSize: 3 },
        extraConfigs: { color: OXXO_DEFAULT_COLORS.ROJO }
      }),

      getCommonWidget({
        commonWidgets: commonWidgets,
        name: 'multipleTrendByWeek',
        title: 'NPS',
        size: [2, 3],
        key: 'nps-multiple-trend-by-week',
        indicator: 'willingness-to-recommend',
        mapper: (data: any) => {
          console.log('Nps widget!')
          console.log(data)
          return data.map((item: any) => {
            const group = item.groupName ?? item.group;
            return {
              ...item,
              groupName: `Sem. ${group} / Encuestas: ${item.count} / NPS: ${item.value}`,
            }
          })
        },
        criticalMomentInclude: [1],
      }),

      getCommonWidget({
        commonWidgets: commonWidgets,
        name: 'rankingByWeek',
        title: 'Satisfacción',
        size: [2, 3],
        key: 'nps-multiple-trend-by-week',
        indicator: 'satisfaction',
        mapper: (data: any) => {
          console.log('satisfaction widget!')
          console.log(data)
          return Immutable.asMutable(data.map((item: any) => {
            const group = item.groupName ?? item.group;
            return {
              ...item,
              label: `Sem. ${group} / Encuestas: ${item.count} `,
            }
          })).sort((item1: any, item2: any) => item2.id - item1.id)
        },
        criticalMomentInclude: [1],
      }),

      ...REGIONES_OBJETIVO.flatMap((reg: any, idx: number) => generateRegionalWidgets(reg, idx, defaultRange)),
    ];

    // 4. Mapeamos e inyectamos
    return generalWidgets.map(widget => injectDefaultDates(widget));
  }
} as DasboardConfig
